import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  ArrowLeft,
  MapPin,
  Calendar,
  Clock,
  Bike,
  ExternalLink,
  Info,
  User,
  Phone,
  Pencil,
  AlertCircle,
  Loader2,
} from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import SEO from "../components/SEO";
import { bikes } from "../mock/mock";
import { computeDurationDetail } from "../lib/duration";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Map Zoho model → fleet image
const findImageForModel = (name) => {
  const n = (name || "").toLowerCase().replace(/[^a-z0-9]/g, "");
  const match = bikes.find((b) => {
    const bn = b.name.toLowerCase().replace(/[^a-z0-9]/g, "");
    return n.includes(bn) || bn.includes(n.split("bs")[0]);
  });
  return match ? match.image : null;
};

// Split "07-Oct-2026 04:00 PM" → { date, time }
const splitZohoDateTime = (dt) => {
  if (!dt) return { date: "", time: "" };
  const idx = dt.indexOf(" ");
  if (idx === -1) return { date: dt, time: "" };
  return {
    date: dt.slice(0, idx).trim(),
    time: dt.slice(idx + 1).trim(),
  };
};

const toISODateFromZoho = (dateStr /* "07-Oct-2026" */, timeStr /* "04:00 PM" */) => {
  if (!dateStr || !timeStr) return null;
  const [d, mon, y] = dateStr.split("-");
  const months = {
    Jan: 0,
    Feb: 1,
    Mar: 2,
    Apr: 3,
    May: 4,
    Jun: 5,
    Jul: 6,
    Aug: 7,
    Sep: 8,
    Oct: 9,
    Nov: 10,
    Dec: 11,
  };
  const [hm, ampm] = timeStr.split(" ");
  let [h, m] = hm.split(":").map(Number);
  if (ampm === "PM" && h < 12) h += 12;
  if (ampm === "AM" && h === 12) h = 0;
  return new Date(Number(y), months[mon], Number(d), h, m);
};

const SCOOTER_QUANTITY = 1; // One scooter per booking — not customer-configurable.
const HELMET_CHOICES = [1, 2];
const DC_CHOICES = ["Yes", "No"];

const Book = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const stateData = location.state || {};
  const qs = useMemo(
    () => new URLSearchParams(location.search),
    [location.search]
  );

  const booking = useMemo(
    () => ({
      pickup_point: stateData.pickup_point || qs.get("pickup") || "",
      start_datetime: stateData.start_datetime || qs.get("start") || "",
      end_datetime: stateData.end_datetime || qs.get("end") || "",
      duration: stateData.duration || qs.get("duration") || "",
      model: stateData.model || qs.get("model") || "",
      unit_price: Number(stateData.unit_price ?? qs.get("price") ?? 0),
      available_quantity: Number(
        stateData.available_quantity ?? qs.get("available") ?? 0
      ),
      // The URL query string that led us here — used for Modify button
      search_qs: stateData.search_qs || "",
    }),
    [stateData, qs]
  );

  // --- Booking options ---
  const [helmets, setHelmets] = useState("");
  const [dcRequired, setDcRequired] = useState("");

  // --- Customer details (temporary state only) ---
  const [name, setName] = useState("");
  const [mobile, setMobile] = useState("");

  const [error, setError] = useState(null);
  const [checking, setChecking] = useState(false);
  const [bookingsOffOpen, setBookingsOffOpen] = useState(false);
  const [policyOpen, setPolicyOpen] = useState(false);

  useEffect(() => {
    if (!booking.model || !booking.pickup_point) {
      navigate("/search", { replace: true });
      return;
    }
    window.scrollTo(0, 0);
  }, [booking.model, booking.pickup_point, navigate]);

  const img = findImageForModel(booking.model);

  // --- Price preview (indicative only; Zoho is authoritative) ---
  const { date: startDate, time: startTime } = useMemo(
    () => splitZohoDateTime(booking.start_datetime),
    [booking.start_datetime]
  );
  const { date: endDate, time: endTime } = useMemo(
    () => splitZohoDateTime(booking.end_datetime),
    [booking.end_datetime]
  );

  const rentalDuration = useMemo(() => {
    const s = toISODateFromZoho(startDate, startTime);
    const e = toISODateFromZoho(endDate, endTime);
    return computeDurationDetail(s, e);
  }, [startDate, startTime, endDate, endTime]);

  const rentalDays = rentalDuration.days;

  // Online bookings require ≥ 24 actual hours.
  const totalRentalHours = useMemo(() => {
    const s = toISODateFromZoho(startDate, startTime);
    const e = toISODateFromZoho(endDate, endTime);
    if (!s || !e) return 0;
    const ms = e.getTime() - s.getTime();
    return ms > 0 ? ms / 3600000 : 0;
  }, [startDate, startTime, endDate, endTime]);
  const belowMinDuration = totalRentalHours > 0 && totalRentalHours < 24;

  const nScooters = SCOOTER_QUANTITY; // Always 1 — matches backend override.
  const nHelmets = parseInt(helmets, 10) || 0;
  const dc = dcRequired === "Yes";

  const rentAmount = (booking.unit_price || 0) * nScooters;

  // Helmet charges:
  //   1 helmet is free. Each extra helmet is ₹50/day,
  //   capped at ₹200 per extra helmet (for rentals > 4 days).
  //   Cap days considered to 30.
  const helmetCharges = useMemo(() => {
    const extra = Math.max(0, nHelmets - 1);
    if (extra === 0) return 0;
    const cappedDays = Math.min(rentalDays, 30);
    const perHelmet = Math.min(cappedDays * 50, 200);
    return extra * perHelmet;
  }, [nHelmets, rentalDays]);

  // Damage Coverage: ₹60 per DC day, where any time beyond a complete
  // 24-hour period counts as a full DC day. Capped at ₹600.
  // Formula: MIN(ceil(actual_duration_hours / 24) × 60, 600).
  // Matches Zoho Creator calculation.
  const dcCharges = useMemo(() => {
    if (!dc) return 0;
    const s = toISODateFromZoho(startDate, startTime);
    const e = toISODateFromZoho(endDate, endTime);
    if (!s || !e || e <= s) return 0;
    const hours = (e - s) / 3600000;
    const dcDays = Math.ceil(hours / 24);
    return Math.min(dcDays * 60, 600);
  }, [dc, startDate, startTime, endDate, endTime]);

  const totalAmount = rentAmount + helmetCharges + dcCharges;

  const modifyHref = booking.search_qs
    ? `/search${booking.search_qs}`
    : "/search";

  const validate = () => {
    if (belowMinDuration)
      return "Minimum rental is 1 full day (24 hours). Please select an end date and time at least 24 hours after your start date and time.";
    if (!helmets || !dcRequired)
      return "Please select the number of helmets and damage coverage.";
    if (!name.trim()) return "Please enter your name.";
    if (!/^\d{10}$/.test(mobile.trim()))
      return "Please enter a valid 10-digit mobile number.";
    if (
      booking.available_quantity > 0 &&
      nScooters > booking.available_quantity
    ) {
      return "This scooter is currently unavailable. Please modify your search.";
    }
    return null;
  };

  const continueToBooking = async () => {
    const err = validate();
    if (err) {
      setError(err);
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }
    setError(null);

    // Hand the booking to the backend — it re-checks the online flag,
    // FORCES No_of_Scooters_Required=1, drops Alternate_Mobile_No, and
    // returns the Zoho redirect URL. The browser never builds this URL
    // itself so quantity cannot be tampered with.
    setChecking(true);
    try {
      const { data } = await axios.post(`${API}/booking/build-zoho-url`, {
        pickup_point: booking.pickup_point || "",
        model: booking.model || "",
        start_date: startDate,
        start_time: startTime,
        end_date: endDate,
        end_time: endTime,
        helmets: nHelmets,
        dc_required: dcRequired,
        name: name.trim(),
        mobile: mobile.trim(),
      });
      window.location.href = data.url;
    } catch (e) {
      setChecking(false);
      if (e?.response?.status === 409) {
        setBookingsOffOpen(true);
        return;
      }
      const d = e?.response?.data?.detail;
      setError(typeof d === "string" ? d : "Could not continue to booking. Please try again.");
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  if (!booking.model) return null;

  const Row = ({ label, value, highlight }) => (
    <div className="flex items-center justify-between py-2 text-sm">
      <span className="text-neutral-500">{label}</span>
      <span
        className={`font-bold ${
          highlight ? "text-neutral-900 text-base" : "text-neutral-900"
        }`}
      >
        {value}
      </span>
    </div>
  );

  return (
    <div className="bg-neutral-50 text-neutral-900 antialiased min-h-screen flex flex-col">
      <SEO
        title="Review Your Booking | RR Bros Bike Rental"
        description="Review your scooter booking details, add helmets and damage coverage, and continue to the official RR Bros booking form."
        canonical="https://ezrental.in/book"
        keywords="scooter booking Udupi, bike booking Manipal, RR Bros booking"
      />
      <Header />

      {/* Breadcrumb */}
      <div className="bg-white border-b border-neutral-100">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex items-center justify-between">
          <Link
            to={modifyHref}
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-neutral-700 hover:text-orange-600 transition-colors"
            data-testid="breadcrumb-back"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Results
          </Link>
          <nav className="hidden sm:flex items-center gap-2 text-xs text-neutral-500">
            <span>Search</span>
            <span className="text-neutral-300">›</span>
            <span className="font-bold text-orange-600">Review &amp; Continue</span>
            <span className="text-neutral-300">›</span>
            <span>Zoho Booking Form</span>
          </nav>
        </div>
      </div>

      <main className="flex-1 py-5 sm:py-8">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 space-y-5 sm:space-y-6">
          {error && (
            <div
              role="alert"
              data-testid="book-error"
              className="bg-red-50 border border-red-200 text-red-800 rounded-xl p-3 flex items-start gap-2 text-sm"
            >
              <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}

          {belowMinDuration && (
            <div
              role="alert"
              data-testid="min-duration-warning"
              className="bg-amber-50 border border-amber-300 text-amber-900 rounded-xl p-3 flex items-start gap-2 text-sm"
            >
              <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5 text-amber-600" />
              <div className="flex-1">
                <p className="font-black">
                  Minimum rental is 1 full day (24 hours).
                </p>
                <p className="text-xs mt-0.5 leading-relaxed">
                  Please select an end date and time at least 24 hours after
                  your start date and time.{" "}
                  <Link
                    to={modifyHref}
                    className="font-black underline underline-offset-2 hover:text-amber-700"
                  >
                    Modify search
                  </Link>
                </p>
              </div>
            </div>
          )}

          {/* YOUR RIDE */}
          <section
            data-testid="your-ride-section"
            className="bg-white rounded-2xl border border-neutral-100 shadow-sm overflow-hidden"
          >
            <div className="flex items-center justify-between px-5 pt-4">
              <h2 className="text-[11px] font-black uppercase tracking-[0.18em] text-orange-600">
                Your Ride
              </h2>
              <Link
                to={modifyHref}
                data-testid="book-modify"
                className="inline-flex items-center gap-1 text-xs font-bold text-orange-600 hover:text-orange-700 uppercase tracking-wider"
              >
                <Pencil className="w-3 h-3" />
                Modify
              </Link>
            </div>

            <div className="flex gap-4 p-5">
              <div className="w-28 h-28 sm:w-32 sm:h-32 rounded-xl bg-gradient-to-br from-neutral-100 to-neutral-50 flex-shrink-0 flex items-center justify-center p-2">
                {img ? (
                  <img
                    src={img}
                    alt={`Rent ${booking.model} at RR Bros ${booking.pickup_point}`}
                    className="w-full h-full object-contain"
                  />
                ) : (
                  <div className="text-neutral-300 text-3xl font-black">RR</div>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-black text-base sm:text-lg leading-tight truncate">
                  {booking.model}
                </h3>
                <div className="flex items-center gap-1.5 mt-1 text-xs text-neutral-600">
                  <MapPin className="w-3.5 h-3.5 text-orange-500 flex-shrink-0" />
                  <span className="font-semibold">
                    {booking.pickup_point}
                  </span>
                </div>
                <div className="mt-2 text-xs text-neutral-600 space-y-0.5">
                  <div className="flex items-center gap-1.5">
                    <Calendar className="w-3.5 h-3.5 text-orange-500 flex-shrink-0" />
                    <span>{booking.start_datetime}</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <Clock className="w-3.5 h-3.5 text-orange-500 flex-shrink-0" />
                    <span>{booking.end_datetime}</span>
                  </div>
                </div>
                <p className="mt-2 text-[11px] font-bold uppercase tracking-wider text-neutral-500">
                  <Bike className="inline w-3 h-3 text-orange-500 mr-1" />
                  {rentalDuration.display}
                </p>
              </div>
            </div>
          </section>

          {/* BOOKING OPTIONS */}
          <section
            data-testid="booking-options-section"
            className="bg-white rounded-2xl border border-neutral-100 shadow-sm p-5 space-y-5"
          >
            <h2 className="text-[11px] font-black uppercase tracking-[0.18em] text-orange-600">
              Booking Options
            </h2>

            {/* No. of Helmets */}
            <div>
              <label className="text-xs font-bold text-neutral-700 mb-1.5 block">
                No. of Helmets
              </label>
              <Select value={helmets} onValueChange={setHelmets}>
                <SelectTrigger
                  className="h-12 bg-neutral-50 rounded-xl"
                  data-testid="book-helmets-trigger"
                >
                  <SelectValue placeholder="Number of helmets required" />
                </SelectTrigger>
                <SelectContent>
                  {HELMET_CHOICES.map((n) => (
                    <SelectItem key={n} value={String(n)}>
                      {n}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p
                className="mt-2 text-[11px] text-neutral-600 leading-relaxed"
                data-testid="helmet-explainer"
              >
                One Helmet is Provided free, Second Helmet is 50rs per Day.
                Maximum Charge is 200rs for more than 4 Days. Max 30 Days.
              </p>

              <div
                data-testid="helmet-safety-notice"
                className="mt-3 rounded-xl overflow-hidden border border-orange-500/70 shadow-sm"
              >
                <div className="flex items-start gap-2 bg-neutral-900 text-white px-3 py-2">
                  <span
                    aria-hidden="true"
                    className="inline-flex items-center justify-center w-6 h-6 rounded-md bg-orange-500 text-white text-sm font-black flex-shrink-0"
                    role="img"
                  >
                    ⚠
                  </span>
                  <p className="text-[11px] sm:text-xs font-black uppercase tracking-[0.14em] text-orange-400 leading-tight pt-0.5">
                    Helmet Rule
                  </p>
                </div>
                <p className="bg-orange-50 text-neutral-900 text-[11px] sm:text-xs leading-relaxed px-3 py-2.5">
                  Travelling from <strong>Udupi</strong> to{" "}
                  <strong>Mulki</strong>, <strong>Padubidri</strong> or{" "}
                  <strong>Mangalore</strong>?{" "}
                  <span className="font-black">Two helmets are mandatory.</span>{" "}
                  Police cameras may catch violations and fines may apply.
                </p>
              </div>
            </div>

            {/* Damage Coverage */}
            <div>
              <label className="text-xs font-bold text-neutral-700 mb-1.5 block">
                Damage Coverage Required
              </label>
              <Select value={dcRequired} onValueChange={setDcRequired}>
                <SelectTrigger
                  className="h-12 bg-neutral-50 rounded-xl"
                  data-testid="book-dc-trigger"
                >
                  <SelectValue placeholder="Damage coverage required" />
                </SelectTrigger>
                <SelectContent>
                  {DC_CHOICES.map((v) => (
                    <SelectItem key={v} value={v}>
                      {v}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p
                className="mt-2 text-[11px] text-neutral-600 leading-relaxed"
                data-testid="dc-explainer"
              >
                Damage Coverage to cover all damages to the vehicle only during
                the rental period (Rs 60/Day)
              </p>
            </div>
          </section>

          {/* CUSTOMER DETAILS */}
          <section
            data-testid="customer-details-section"
            className="bg-white rounded-2xl border border-neutral-100 shadow-sm p-5 space-y-4"
          >
            <h2 className="text-[11px] font-black uppercase tracking-[0.18em] text-orange-600">
              Customer Details
            </h2>

            <div>
              <label className="text-xs font-bold text-neutral-700 mb-1.5 block">
                Name
              </label>
              <div className="relative">
                <User className="w-4 h-4 text-orange-500 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Enter your name"
                  autoComplete="name"
                  data-testid="book-name"
                  className="h-12 w-full pl-10 pr-3 rounded-xl bg-neutral-50 border border-neutral-200 text-sm font-semibold text-neutral-900 outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                />
              </div>
            </div>

            <div>
              <label className="text-xs font-bold text-neutral-700 mb-1.5 block">
                Mobile
              </label>
              <div className="relative">
                <Phone className="w-4 h-4 text-orange-500 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                <input
                  type="tel"
                  inputMode="numeric"
                  pattern="[0-9]{10}"
                  maxLength={10}
                  value={mobile}
                  onChange={(e) =>
                    setMobile(e.target.value.replace(/\D/g, "").slice(0, 10))
                  }
                  placeholder="10-digit mobile number"
                  autoComplete="tel"
                  data-testid="book-mobile"
                  className="h-12 w-full pl-10 pr-3 rounded-xl bg-neutral-50 border border-neutral-200 text-sm font-semibold text-neutral-900 outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                />
              </div>
            </div>
          </section>

          {/* DRIVING LICENCE WARNING — informational only, placed between
              Customer Details and Price Summary. */}
          <section
            role="note"
            data-testid="licence-warning-section"
            className="rounded-2xl border border-orange-500 bg-orange-50 p-3 sm:p-4 flex items-start gap-2.5 shadow-sm"
          >
            <span
              aria-hidden="true"
              className="inline-flex items-center justify-center w-7 h-7 rounded-md bg-orange-500 text-white text-sm font-black flex-shrink-0"
            >
              ⚠
            </span>
            <div className="min-w-0">
              <p className="text-[11px] sm:text-xs font-black uppercase tracking-[0.14em] text-orange-700 leading-tight">
                Important · Driving Licence Mandatory
              </p>
              <p className="mt-1 text-[12px] sm:text-sm leading-snug text-neutral-900">
                A valid two-wheeler driving licence with{" "}
                <strong>MCWG / MCWOG</strong> category is mandatory at vehicle
                pickup. Failure to produce it will result in booking
                cancellation without a refund.
              </p>
            </div>
          </section>

          {/* PRICE SUMMARY */}
          <section
            data-testid="price-summary-section"
            className="bg-white rounded-2xl border border-neutral-100 shadow-sm p-5"
          >
            <h2 className="text-[11px] font-black uppercase tracking-[0.18em] text-orange-600 mb-2">
              Price Summary
            </h2>

            <div className="divide-y divide-neutral-100">
              <Row
                label="Rent Amount"
                value={`₹${rentAmount.toLocaleString("en-IN")}`}
              />
              <Row
                label="Helmet Charges"
                value={`₹${helmetCharges.toLocaleString("en-IN")}`}
              />
              <Row
                label="Damage Coverage"
                value={`₹${dcCharges.toLocaleString("en-IN")}`}
              />
            </div>

            <div className="mt-3 pt-3 border-t-2 border-neutral-900 flex items-center justify-between">
              <span className="text-sm font-black text-neutral-900 uppercase tracking-wider">
                Total Amount
              </span>
              <span
                className="text-2xl font-black text-neutral-900"
                data-testid="book-total"
              >
                ₹{totalAmount.toLocaleString("en-IN")}
              </span>
            </div>

            <p
              className="mt-4 text-[11px] text-neutral-600 leading-relaxed"
              data-testid="advance-note"
            >
              Advance amount of Rs 500 per scooter will be taken as
              pre-booking amount and will be adjusted with the total Amount
              during the rental pickup.{" "}
              <button
                type="button"
                onClick={() => setPolicyOpen(true)}
                data-testid="refund-policy-link"
                className="text-orange-600 font-semibold underline underline-offset-2 hover:text-orange-700"
              >
                Refund &amp; Cancellation Policy
              </button>
            </p>
          </section>

          {/* CONTINUE */}
          <div className="space-y-3">
            <button
              type="button"
              onClick={continueToBooking}
              disabled={checking || belowMinDuration}
              data-testid="book-continue"
              className="w-full h-14 rounded-xl bg-orange-500 hover:bg-orange-600 disabled:bg-neutral-300 text-white font-black text-sm uppercase tracking-wider inline-flex items-center justify-center gap-2 shadow-lg transition-colors"
            >
              {checking ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <ExternalLink className="w-4 h-4" />
              )}
              {checking ? "Checking availability…" : "Continue to Booking"}
            </button>

            <div className="text-[11px] text-neutral-500 flex items-start gap-1.5 justify-center">
              <Info className="w-3 h-3 text-neutral-400 flex-shrink-0 mt-0.5" />
              <span>
                By continuing you agree to our{" "}
                <Link
                  to="/terms"
                  className="text-orange-600 font-semibold underline"
                  target="_blank"
                >
                  Terms &amp; Conditions
                </Link>
                .
              </span>
            </div>
          </div>
        </div>
      </main>

      <Footer />

      <AlertDialog open={bookingsOffOpen} onOpenChange={setBookingsOffOpen}>
        <AlertDialogContent data-testid="bookings-off-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>
              Online bookings are temporarily unavailable
            </AlertDialogTitle>
            <AlertDialogDescription>
              Our online booking is paused right now. To book your scooter,
              please call us at <strong>070677 06719</strong>.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex flex-col-reverse sm:flex-row gap-2 sm:gap-3">
            <AlertDialogAction
              asChild
              data-testid="bookings-off-call"
              className="bg-orange-500 hover:bg-orange-600"
            >
              <a href="tel:+917067706719">
                <Phone className="w-4 h-4 mr-1.5" />
                Call 070677 06719
              </a>
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={policyOpen} onOpenChange={setPolicyOpen}>
        <DialogContent
          data-testid="refund-policy-dialog"
          className="max-w-md"
        >
          <DialogHeader>
            <DialogTitle>Refund &amp; Cancellation Policy</DialogTitle>
          </DialogHeader>
          <div className="text-sm text-neutral-700 space-y-2">
            <p className="font-semibold text-neutral-900">
              If you cancel your booking:
            </p>
            <ul className="space-y-1.5 text-[13px] leading-relaxed">
              <li className="flex gap-2">
                <span className="text-orange-500 font-black">•</span>
                <span>
                  <strong>No-show:</strong> 100% of the rental amount will be
                  deducted.
                </span>
              </li>
              <li className="flex gap-2">
                <span className="text-orange-500 font-black">•</span>
                <span>
                  <strong>Cancellation within 0–6 hours before pickup:</strong>{" "}
                  75% will be deducted.
                </span>
              </li>
              <li className="flex gap-2">
                <span className="text-orange-500 font-black">•</span>
                <span>
                  <strong>Cancellation 6–24 hours before pickup:</strong> 50%
                  will be deducted.
                </span>
              </li>
              <li className="flex gap-2">
                <span className="text-orange-500 font-black">•</span>
                <span>
                  <strong>Cancellation 24–72 hours before pickup:</strong> 25%
                  will be deducted.
                </span>
              </li>
              <li className="flex gap-2">
                <span className="text-orange-500 font-black">•</span>
                <span>
                  <strong>Cancellation 72+ hours before pickup:</strong> 10%
                  will be deducted.
                </span>
              </li>
            </ul>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Book;
